import React from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Typography, CircularProgress, Box, IconButton, Popover,
  TextField, Button, Tooltip, Collapse
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { parseModelResponse } from '../utils/parseModelResponse';
import { hasManualChanges } from '../utils/tableDataUtils';

const hidedProviderLabels = ['Variant A', 'Variant B', 'Variant C', 'Variant D', 'Variant E', 'Variant F', 'Variant G'];

const norm = (v) => String(v ?? '')
  .normalize('NFKC')
  .replace(/\s+/g, ' ')
  .trim();

const makeQueryVariants = (raw) => {
  const full = norm(raw);
  if (!full) return [];

  const qPos = full.indexOf('?');
  const leftWithQ = qPos !== -1 ? (full.slice(0, qPos + 1).trim()) : '';
  const rightAfterQ = qPos !== -1 ? (full.slice(qPos + 1).trim()) : '';

  const tail = full.length > 40 ? full.slice(-40).trim() : '';

  const variants = [full];
  if (leftWithQ) variants.push(leftWithQ);
  if (rightAfterQ) variants.push(rightAfterQ);
  if (tail && tail !== rightAfterQ) variants.push(tail);

  return Array.from(new Set(variants.filter(Boolean)));
};

const includesAny = (hay, variants) => {
  const h = norm(hay);
  if (!h) return false;
  return variants.some(v => v && h.includes(norm(v)));
};

const docRefToString = (ref) => {
  if (!ref) return '';
  const arr = Array.isArray(ref) ? ref : [ref];
  return arr
    .map(x => String(x ?? '')
      .replace(/^https?:\/\/(?:www\.)?/i, '')
    )
    .join(' ');
};

const extractDocRefOnly = (parsed, rowIndex) => {
  if (!parsed) return '';
  const stepIndex = rowIndex - 1;
  const step = parsed.treatment_steps?.[stepIndex];
  if (!step) return '';
  return docRefToString(step.doc_ref);
};


const extractStepBodyNoRef = (parsed, rowIndex) => {
  if (!parsed) return '';
  const stepIndex = rowIndex - 1;
  const step = parsed.treatment_steps?.[stepIndex];
  if (!step) return '';
  return [step.step ?? '', step.note ?? ''].join(' ');
};

const ResponseTableView = ({
  responses, providersLabels, selectedProviders, rowLabels,
  cellStates, comments, anchorEl, tempComment,
  sending, sentSuccess, cellRefs,
  onMark, onSaveComment, onSendClick, setTempComment,
  getCellColor, getColumnColor, isColumnComplete, responseTimes, activeTimers, setAnchorEl, userRole, selectedText, onSelectText
}) => {
  const isPrivileged = userRole === 'admin' || userRole === 'user';

  const renderHighlightedText = (text, queryOrQueries) => {
    if (!text) return text;
    const src = String(text);

    const queries = Array.isArray(queryOrQueries)
      ? queryOrQueries.filter(Boolean)
      : [String(queryOrQueries || '')].filter(Boolean);

    if (queries.length === 0) return src;

    const esc = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = queries
      .map(q => q.trim())
      .filter(Boolean)
      .sort((a,b) => b.length - a.length)
      .map(esc);
    const re = new RegExp(`(${parts.join('|')})`, 'iu');
    const chunks = src.split(re);
    return chunks.map((part, i) =>
      i % 2 === 1
        ? <mark key={i} style={{ backgroundColor: '#fff176', padding: '0 2px' }}>{part}</mark>
        : <React.Fragment key={i}>{part}</React.Fragment>
    );
  };

  const renderHighlighted = (text) => renderHighlightedText(text, makeQueryVariants(selectedText));

  const effectiveProvidersLabels = providersLabels;
  // const effectiveProvidersLabels = isPrivileged ? providersLabels : hidedProviderLabels;

  const isProviderVisible = (colIndex) => {
    const providerLabel = effectiveProvidersLabels[colIndex];
    return selectedProviders.includes(providerLabel);
  };

  const [tooltipText, setTooltipText] = React.useState('');
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [computedCellStates, setComputedCellStates] = React.useState({});
  const tooltipRaf = React.useRef(null);
  const pendingMousePos = React.useRef({ x: 0, y: 0 });
  const tooltipDelayTimer = React.useRef(null);

  const isCtrlPressedRef = React.useRef(false);

  const [flashCellKey, setFlashCellKey] = React.useState(null);
  const flashTimerRef = React.useRef(null);
  React.useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const extractSearchText = (parsed, rowIndex) => {
    if (!parsed) return '';
    if (rowIndex === 0) return `${parsed.title || ''}`;
    if (rowIndex === (rowLabels.length - 2)) return `${parsed.description || ''}`;
    const stepIndex = rowIndex - 1;
    const step = parsed.treatment_steps?.[stepIndex];
    if (!step) return '';
    return [step.step ?? '', step.note ?? '', docRefToString(step.doc_ref)].join(' ');
  };

  const normalizeDocRefArray = (r) => {
    if (!r) return [];
    if (Array.isArray(r)) {
      return r.map(x => norm(x)).filter(Boolean);
    }
    return [norm(r)].filter(Boolean);
  };

  const normalizeStepForCompare = (s) => {
    if (!s) {
      return {
        step: '',
        note: '',
        docRefs: []
      };
    }

    const cleanupNumbering = (txt) => {
      const t = norm(txt || '').toLowerCase();
      return t.replace(/^\d+[\).\-\:\s]+/, '').trim();
    };

    return {
      step: cleanupNumbering(s.step || ''),
      note: cleanupNumbering(s.note || ''),
      docRefs: normalizeDocRefArray(s.doc_ref).sort()
    };
  };

  const stepsEqual = (a, b) => {
    if (!a || !b) return false;
    const na = normalizeStepForCompare(a);
    const nb = normalizeStepForCompare(b);
    if (na.step !== nb.step) return false;
    if (na.note !== nb.note) return false;
    if (na.docRefs.length !== nb.docRefs.length) return false;
    for (let i = 0; i < na.docRefs.length; i++) {
      if (na.docRefs[i] !== nb.docRefs[i]) return false;
    }
    return true;
  };

  function mapTypeToGroup(stepType) {
    if (!stepType) return null;
    const t = String(stepType).toLowerCase().trim();
    if (t === 'treatment' || t === 'matched' || t === 'done' || t === 'performed') {
      return 'matched_treatments';
    }
    if (t === 'unmatched' || t === 'extra' || t === 'other') {
      return 'unmatched_treatments';
    }
    if (t === 'suggested' || t === 'plan' || t === 'todo' || t === 'recommendation') {
      return 'suggested_steps';
    }
    return null;
  }

  const getStepGroup = (parsed, step) => {
    if (!step) return null;
    if (step.source === 'matched') return 'matched_treatments';
    if (step.source === 'unmatched') return 'unmatched_treatments';
    if (step.source === 'suggested') return 'suggested_steps';
    if (step.type) {
      return mapTypeToGroup(step.type);
    }
    if (!parsed) return null;
    const mArr = Array.isArray(parsed.matched_treatments) ? parsed.matched_treatments : [];
    const uArr = Array.isArray(parsed.unmatched_treatments) ? parsed.unmatched_treatments : [];
    const sArr = Array.isArray(parsed.suggested_steps) ? parsed.suggested_steps : [];
    if (mArr.some(s => stepsEqual(s, step))) return 'matched_treatments';
    if (uArr.some(s => stepsEqual(s, step))) return 'unmatched_treatments';
    if (sArr.some(s => stepsEqual(s, step))) return 'suggested_steps';
    return null;
  };

  const getStepMainTextSx = (group) => {
    switch (group) {
      case 'matched_treatments':
        return {
          fontWeight: 700,
          color: '#000',
          fontStyle: 'normal'
        };
      case 'unmatched_treatments':
        return {
          fontWeight: 400,
          color: '#000',
          fontStyle: 'normal'
        };
      case 'suggested_steps':
        return {
          fontWeight: 700,
          color: '#1565c0',
          fontStyle: 'italic'
        };
      default:
        return {
          fontWeight: 400,
          color: 'inherit',
          fontStyle: 'normal'
        };
    }
  };

  if (typeof window !== 'undefined') {
    try {
      const fp = (() => { try { return parseModelResponse(responses[0]); } catch { return null; } })();
    } catch (e) {
    }
  }

  const [descExpanded, setDescExpanded] = React.useState({});

  const toggleDesc = (colIndex) => {
    setDescExpanded(prev => ({ ...prev, [colIndex]: !prev[colIndex] }));
  };

  const setDescOpen = (colIndex, open) => {
    setDescExpanded(prev => (prev[colIndex] === open ? prev : { ...prev, [colIndex]: open }));
  };


  React.useEffect(() => {
    const queries = makeQueryVariants(selectedText);
    if (queries.length === 0) return;

    let targetKey = null;

    outer_docref:
    for (let colIndex = 0; colIndex < responses.length; colIndex++) {
      if (!isProviderVisible(colIndex)) continue;
      const parsed = parseModelResponse(responses[colIndex]);
      if (!parsed) continue;
      for (let rowIndex = 1; rowIndex < rowLabels.length - 2; rowIndex++) {
        const hay = extractDocRefOnly(parsed, rowIndex);
        if (includesAny(hay, queries)) {
          targetKey = `${rowIndex}_${colIndex}`;
          break outer_docref;
        }
      }
    }

    if (!targetKey) {
      outer_step_body:
      for (let colIndex = 0; colIndex < responses.length; colIndex++) {
        if (!isProviderVisible(colIndex)) continue;
        const parsed = parseModelResponse(responses[colIndex]);
        if (!parsed) continue;
        for (let rowIndex = 1; rowIndex < rowLabels.length - 2; rowIndex++) {
          const hay = extractStepBodyNoRef(parsed, rowIndex);
          if (includesAny(hay, queries)) {
            targetKey = `${rowIndex}_${colIndex}`;
            break outer_step_body;
          }
        }
      }
    }

    if (!targetKey) {
      outer_rest:
      for (let colIndex = 0; colIndex < responses.length; colIndex++) {
        if (!isProviderVisible(colIndex)) continue;
        const parsed = parseModelResponse(responses[colIndex]);
        if (!parsed) continue;

        if (includesAny(parsed.title || '', queries)) {
          targetKey = `0_${colIndex}`;
          break outer_rest;
        }
        const descRow = rowLabels.length - 2;
        if (includesAny(parsed.description || '', queries)) {
          targetKey = `${descRow}_${colIndex}`;
          break outer_rest;
        }
      }
    }

    if (!targetKey) {
      console.log('[RTV][scroll] no found match', JSON.stringify(queries));
      return;
    }

    const [r, c] = targetKey.split('_').map(Number);
    const descRow = rowLabels.length - 2;
    if (r === descRow) setDescOpen(c, true);


    const el = cellRefs.current[targetKey];
    if (!el || typeof el.scrollIntoView !== 'function') return;

    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    } catch {
      el.scrollIntoView();
    }

    setFlashCellKey(targetKey);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashCellKey(null), 1300);

  }, [selectedText, responses, rowLabels, cellRefs, selectedProviders]);

  React.useEffect(() => {
    const updated = { ...cellStates };

    responses.forEach((response, colIndex) => {
      const parsed = parseModelResponse(response);
      if (!parsed) return;

      rowLabels.forEach((rowLabel, rowIndex) => {
        if (rowLabel === 'Send') return;

        const cellKey = `${rowIndex}_${colIndex}`;
        const comment = comments[cellKey];
        const currentState = updated[cellKey];

        let approved = null;
        let doctorsComment = '';

        if (rowIndex === 0) {
          approved = parsed.title_approved;
          doctorsComment = parsed.title_comment || '';
        } else if (rowIndex === rowLabels.length - 2) {
          approved = parsed.description_approved;
          doctorsComment = parsed.description_comment || '';
        } else {
          const stepIndex = rowIndex - 1;
          const step = parsed.treatment_steps?.[stepIndex];
          approved = step?.approved;
          doctorsComment = step?.doctorsComment || '';
        }

        if (
          approved === false &&
          (!doctorsComment || doctorsComment.trim() === '') &&
          (!comment || comment.trim() === '') &&
          currentState === undefined
        ) {
          updated[cellKey] = 'bad';
        }
      });
    });

    setComputedCellStates(updated);
  }, [responses, comments, rowLabels, cellStates]);

  React.useEffect(() => {
    const down = e => { if (e.key === 'Control') isCtrlPressedRef.current = true; };
    const up = e => { if (e.key === 'Control') isCtrlPressedRef.current = false; };

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  React.useEffect(() => {
    responses.forEach((response, colIndex) => {
      const parsed = parseModelResponse(response);
      if (!parsed) return;

      rowLabels.forEach((rowLabel, rowIndex) => {
        if (rowLabel === 'Send') return;
        const stepIndex = rowIndex - 1;
        if (rowIndex > 0 && rowIndex < rowLabels.length - 2) {
          const step = parsed.treatment_steps?.[stepIndex];
        }
      });
    });
  }, [responses, comments, rowLabels, cellStates]);

  const formatDocRef = (ref) => {
    if (!ref) return null;

    const renderOne = (r, key) => {
      let s = typeof r === 'string' ? r : String(r);
      const isUrl = /^https?:\/\//i.test(s);
      const highlighted = renderHighlightedText(s, selectedText);
      return isUrl ? (
        <a key={key} href={s} target="_blank" rel="noreferrer">
          {highlighted}
        </a>
      ) : (
        <span key={key}>{highlighted}</span>
      );
    };

    return Array.isArray(ref)
      ? ref.map((r, i) => (
          <React.Fragment key={i}>
            {i > 0 && ' · '}
            {renderOne(r, i)}
          </React.Fragment>
        ))
      : renderOne(ref, 0);
  };
  
  const isSendButtonEnabled = (colIndex) => {
    const response = responses[colIndex];
    const parsed = parseModelResponse(response);
    if (!parsed) return false;

    for (let rowIndex = 0; rowIndex < rowLabels.length; rowIndex++) {
      if (rowLabels[rowIndex] === 'Send') continue;

      const cellKey = `${rowIndex}_${colIndex}`;
      const state = cellStates[cellKey];
      const manualComment = (comments[cellKey] || '').trim();
      let doctorsComment = '';
      if (rowIndex === 0) {
        doctorsComment = parsed.title_comment || '';
      } else if (rowIndex === rowLabels.length - 2) {
        doctorsComment = parsed.description_comment || '';
      } else {
        const stepIndex = rowIndex - 1;
        const step = parsed.treatment_steps?.[stepIndex];
        doctorsComment = step?.doctorsComment || '';
      }
      const doctorsCommentTrimmed = (doctorsComment || '').trim();
      const isExplicitlyReviewed = (state === 'ok' || state === 'bad');
      if (!isExplicitlyReviewed) {
        return false;
      }

      if (state === 'bad') {
        const hasAnyComment = !!(manualComment || doctorsCommentTrimmed);
        if (!hasAnyComment) {
          return false;
        }
      }
    }
    return true;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ border: '1px solid #ccc', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', p: 1 }}>
        <Table
          size="small"
          sx={{
            minWidth: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            borderCollapse: 'separate',
            borderSpacing: 0
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: 'lightblue' }}>
              <TableCell sx={{ fontWeight: 700, backgroundColor: '#e0f2f1', borderTopLeftRadius: 12 }}>LLMs</TableCell>
              {effectiveProvidersLabels.map((label, i) =>
                selectedProviders.includes(label) && (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 700,
                      backgroundColor: getColumnColor(i),
                      ...(i === effectiveProvidersLabels.length - 1 && { borderTopRightRadius: 12 })
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowLabels.map((rowLabel, rowIndex) => {
              if (rowLabel === 'Send') return null;

              return (
                <TableRow key={rowIndex}>
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#e0f2f1', whiteSpace: 'nowrap' }}>
                    {rowLabel}
                  </TableCell>
                  {responses.map((response, colIndex) => {
                    const providerLabel = effectiveProvidersLabels[colIndex];
                    if (!selectedProviders.includes(providerLabel)) return null;

                    const cellKey = `${rowIndex}_${colIndex}`;
                    if (response === null) {
                      return (
                        <TableCell key={colIndex} sx={{ textAlign: 'center' }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      );
                    }

                    const parsed = parseModelResponse(response);
                    if (!parsed) {
                      return (
                        <TableCell key={colIndex} sx={{ color: 'gray' }}>—</TableCell>
                      );
                    }

                    let content = '—';
                    let approved = null;
                    let doctorsComment = '';
                    const isTitle = rowIndex === 0;
                    const isDescription = rowIndex === rowLabels.length - 2;

                    if (isTitle) {
                      content = (
                        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
                          {renderHighlighted(parsed.title)}
                        </Typography>
                      );
                      approved = parsed.title_approved;
                      doctorsComment = parsed.title_comment || '';
                    } else if (isDescription) {
                      const isTitle = rowIndex === 0;
                      const isDescription = rowIndex === rowLabels.length - 2;

                      const descIsOpen = isDescription ? !!descExpanded[colIndex] : false;
                      const isDescCollapsed = isDescription && !descIsOpen;

                      content = (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 3 }}>
                          <IconButton
                            size="large"
                            onClick={(e) => { e.stopPropagation(); toggleDesc(colIndex); }}
                            sx={{ p: 0.5, width: 42, height: 42 }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 34,
                                transform: descIsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                transition: 'transform 0.15s ease',
                              }}
                            />
                          </IconButton>

                            {/* content: fully hidden when collapsed */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {descIsOpen && (
                              <Typography sx={{ whiteSpace: 'pre-line' }}>
                                {renderHighlighted(parsed.description)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                      approved = parsed.description_approved;
                      doctorsComment = parsed.description_comment || '';
                    } else {
                      const stepIndex = rowIndex - 1;
                      const step = parsed.treatment_steps?.[stepIndex];
                      if (step) {
                        const group = getStepGroup(parsed, step);
                        const mainTextSx = getStepMainTextSx(group);
                        content = (
                          <Box
                            onClick={() => {
                              const refStr = docRefToString(step.doc_ref);
                              if (refStr) {
                                onSelectText?.(refStr);
                              }
                            }}
                          >
                            <Typography sx={mainTextSx}>
                              {renderHighlighted(step.step)}
                            </Typography>
                            {/* group label hidden */}
                            {false && group && (
                              <Typography sx={{ fontSize: '0.7rem', color: '#888', fontStyle: 'italic' }}>
                                {group}
                              </Typography>
                            )}
                            {step.note && (
                              <Typography sx={{ fontWeight: 400, color: 'inherit', fontStyle: 'normal' }}>
                                {renderHighlighted(step.note)}
                              </Typography>
                            )}
                            {step.doc_ref && (
                              <Typography sx={{ fontSize: '0.75em', color: '#1565c0', mt: 0.5 }}>
                                Ref: {formatDocRef(step.doc_ref)}
                              </Typography>
                            )}
                          </Box>
                        );
                        approved = step.approved;
                        doctorsComment = step.doctorsComment || '';
                      }
                    }

                    const tooltip = comments[cellKey] || (!approved && doctorsComment ? doctorsComment : '');
                    const usernameTooltip = parsed?.username ? `@${parsed.username}` : '';
                    const currentTooltip = isCtrlPressedRef.current && usernameTooltip ? usernameTooltip : tooltip;

                    const manualState = computedCellStates[cellKey];
                    const comment = comments[cellKey];
                    const hasAnyComment = !!(comment?.trim() || doctorsComment?.trim());

                    const cellColor = (() => {
                      if (manualState === 'bad') {
                        return hasAnyComment ? '#fdeaea' : '#f1b0b7';
                      }
                      if (manualState === 'ok') {
                        return '#e6f4ea';
                      }
                      if (approved === true) {
                        return '#e6f4ea';
                      }
                      if (approved === false) {
                        return hasAnyComment ? '#fdeaea' : '#f1b0b7';
                      }
                      if (approved == null) {
                        return hasAnyComment ? '#fdeaea' : 'white';
                      }
                      return 'white';
                    })();

                    return (
                      <TableCell
                        key={colIndex}
                        ref={(el) => { cellRefs.current[cellKey] = el; }}
                        sx={{
                          backgroundColor: cellColor,
                          verticalAlign: 'top',
                          cursor: 'default',
                          outline: flashCellKey === cellKey ? '3px solid #ffeb3b' : 'none',
                          transition: 'outline 0.2s ease',
                        }}
                        onMouseMove={(e) => {
                          if (!currentTooltip) return;
                          pendingMousePos.current = { x: e.clientX + 12, y: e.clientY + 12 };

                          if (!showTooltip && !tooltipDelayTimer.current) {
                            tooltipDelayTimer.current = setTimeout(() => {
                              setShowTooltip(true);
                              setTooltipText(currentTooltip);
                              tooltipDelayTimer.current = null;
                            }, 200);
                          }
                          if (!tooltip) return;

                          pendingMousePos.current = { x: e.clientX + 12, y: e.clientY + 12 };

                        // Start fade-in timer
                          if (!showTooltip && !tooltipDelayTimer.current) {
                            tooltipDelayTimer.current = setTimeout(() => {
                              setShowTooltip(true);
                              setTooltipText(tooltip);
                              tooltipDelayTimer.current = null;
                          }, 200); // 200 ms delay before showing
                          }

                          if (!tooltipRaf.current) {
                            tooltipRaf.current = requestAnimationFrame(() => {
                              setTooltipPos(pendingMousePos.current);
                              tooltipRaf.current = null;
                            });
                          }
                        }}
                        onMouseLeave={() => {
                          setShowTooltip(false);
                          setTooltipText('');
                          if (tooltipDelayTimer.current) {
                            clearTimeout(tooltipDelayTimer.current);
                            tooltipDelayTimer.current = null;
                          }
                          if (tooltipRaf.current) {
                            cancelAnimationFrame(tooltipRaf.current);
                            tooltipRaf.current = null;
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateRows: 'auto 1fr auto',
                            height: '100%',
                            width: '100%',
                            rowGap: 0.5
                          }}
                        >
                          {/* text */}
                          <Box sx={{ justifySelf: 'start' }}>
                            {content}
                          </Box>
                          {/* spacer */}
                          <Box />
                          {/* buttons */}
                          <Box sx={{ justifySelf: 'end', display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => onMark(rowIndex, colIndex, 'ok')}
                              sx={{ border: '1px solid green', p: '3px', width: 30, height: 30 }}
                            >
                              <CheckIcon sx={{ fontSize: 21, color: 'green' }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => onMark(rowIndex, colIndex, 'bad', e)}
                              sx={{ border: '1px solid red', p: '3px', width: 30, height: 30 }}
                            >
                              <CloseIcon sx={{ fontSize: 21, color: 'red' }} />
                            </IconButton>
                            {manualState === 'bad' &&
                              (!comment || comment.trim() === '') &&
                              (!doctorsComment || doctorsComment.trim() === '') && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={(e) => onMark(rowIndex, colIndex, 'bad', e)}
                                  sx={{
                                    px: 1, py: '3px',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    minWidth: 'auto',
                                    borderColor: '#8B0000',
                                    color: '#8B0000',
                                    textTransform: 'none'
                                  }}
                                >
                                  Add comment
                                </Button>
                              )}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell sx={{ fontWeight: 700, backgroundColor: '#e0f2f1' }}>Send</TableCell>
              {responses.map((_, colIndex) => {
                const providerLabel = effectiveProvidersLabels[colIndex];
                if (!selectedProviders.includes(providerLabel)) return null;
                return (
                  <TableCell key={colIndex}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip
                          title={
                            !isSendButtonEnabled(colIndex)
                              ? 'Not all comments set!'
                              : ''
                          }
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="success"
                              disabled={!isSendButtonEnabled(colIndex) || sending[colIndex]}
                              onClick={() => onSendClick(colIndex)}
                            >
                              Send
                            </Button>
                          </span>
                        </Tooltip>
                        {sending[colIndex] && <CircularProgress size={20} />}
                        {sentSuccess[colIndex] && !sending[colIndex] && (
                          <Typography sx={{ color: 'green', fontWeight: 700, fontSize: 32 }}>✔</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {showTooltip && tooltipText && (
        <Box
          sx={{
            position: 'fixed',
            top: tooltipPos.y,
            left: tooltipPos.x,
            backgroundColor: '#fdeaea',
            border: '1px solid #999',
            padding: '8px 12px',
            fontSize: '1.2rem',
            color: 'black',
            borderRadius: 2,
            maxWidth: 400,
            whiteSpace: 'pre-wrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            zIndex: 9999,
            pointerEvents: 'none',
            opacity: 1,
            transition: 'top 0.05s linear, left 0.05s linear, opacity 0.2s ease-in'
          }}
        >
          {tooltipText}
        </Box>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: '#fbe9e9',
            p: 2,
            minWidth: anchorEl?.offsetWidth || 200,
            maxWidth: anchorEl?.offsetWidth || 400,
            minHeight: 100,
            p: 0
          }
        }}
        onClose={() => setAnchorEl(null)}
      >
        <Box sx={{ px: 1.5, pt: 0.5, pb: 1.5 }}>
          {/* close button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <IconButton size="small" onClick={() => setAnchorEl(null)}
              sx={{
                backgroundColor: '#f8d7da',
                '&:hover': {
                  backgroundColor: '#f1b0b7',
                }
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: '#d9534f' }} />
            </IconButton>
          </Box>

          <TextField
            multiline
            fullWidth
            label="Enter comment"
            size="small"
            value={tempComment}
            onChange={(e) => setTempComment(e.target.value)}
            sx={{ maxWidth: '100%' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={onSaveComment}
              disabled={!tempComment.trim()}
            >
              Save comment
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default ResponseTableView;